import {
    Box,
    HStack,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { FiCheck, FiGlobe } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const SUPPORTED_LANGUAGES = [
    { code: 'en', labelKey: 'language.en' },
    { code: 'tr', labelKey: 'language.tr' },
] as const;

type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

interface LanguageSwitcherProps {
    /** Icon-only trigger for tight slots like the status bar. */
    compact?: boolean;
}

/**
 * Language switcher: flips the app between its supported languages via
 * i18n.changeLanguage. The choice is persisted by the i18next language
 * detector (localStorage cache) and restored on the next launch. The
 * dropdown auto-sizes to its widest label, so longer language names never
 * clip.
 */
export function LanguageSwitcher({ compact = false }: LanguageSwitcherProps) {
    const { t, i18n } = useTranslation();
    const baseCode = (i18n.language || '').split('-')[0] as LanguageCode;
    const isActive = (code: LanguageCode) => code === baseCode;

    return (
        <Menu>
            <Tooltip label={t('common.language')} openDelay={300}>
                <MenuButton
                    px={compact ? 1.5 : 2}
                    py={0.5}
                    borderRadius="sm"
                    fontSize="xs"
                    color="icon.base"
                    _hover={{ bg: 'bg.hover', color: 'text.primary' }}
                    aria-label={t('common.language')}
                >
                    <HStack spacing={1.5}>
                        <Icon as={FiGlobe} boxSize={compact ? 3.5 : 4} flexShrink={0} />
                        {!compact && (
                            <Text as="span" whiteSpace="nowrap">
                                {t(`language.${baseCode}`)}
                            </Text>
                        )}
                    </HStack>
                </MenuButton>
            </Tooltip>
            <MenuList minW="max-content">
                {SUPPORTED_LANGUAGES.map(({ code, labelKey }) => (
                    <MenuItem
                        key={code}
                        icon={
                            <Box w="12px" display="flex">
                                {isActive(code) && <Icon as={FiCheck} boxSize={3} />}
                            </Box>
                        }
                        onClick={() => i18n.changeLanguage(code)}
                    >
                        {t(labelKey)}
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    );
}
